import { useEffect, useState } from "react";

import styles from "../../styles/components/AdminDetailModals.module.css";
import ModalOverlay from "../../ui/Modals/ModalOverlay";
import BaseInput from "../../ui/inputs/BaseInput/BaseInput";
import DropdownSelect from "../../ui/inputs/DropdownSelect";
import TimezoneSelect from "../../ui/inputs/TimezoneSelect";
import Toggle from "../../ui/selectors/Toggle";
import { accountsService } from "../../services/accounts.service";
import { usersService } from "../../services/users.service";
import { extractApiErrorMessage } from "../../utils/apiError";
import { sortAccountsByName } from "../../utils/accountOptions";
import MailAccountAssignmentFlags, { EMPTY_ASSIGNMENT_DRAFT } from "./MailAccountAssignmentFlags";
import { userDisplayName } from "./mailAccountAdminHelpers";

const EMPTY_DRAFT = {
  name: "",
  email: "",
  password: "",
  smtp: "",
  smtp_port: "",
  imap: "",
  imap_port: "",
  timezone: "",
};

export default function CreateMailAccountModal({ accounts = [], defaultAccountId = "", onClose, onCreated }) {
  const [accountId, setAccountId] = useState(defaultAccountId ? String(defaultAccountId) : "");
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [assignmentDraft, setAssignmentDraft] = useState(EMPTY_ASSIGNMENT_DRAFT);
  const [keepOwnAccess, setKeepOwnAccess] = useState(false);
  const [accountUsers, setAccountUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [createdMailAccount, setCreatedMailAccount] = useState(null);

  useEffect(() => {
    if (!accountId) {
      setAccountUsers([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingUsers(true);
    setAssignmentDraft((current) => ({ ...current, userId: "" }));
    accountsService
      .listUsers(Number(accountId), { includeInherited: true })
      .then((users) => {
        if (!cancelled) setAccountUsers(Array.isArray(users) ? users : []);
      })
      .catch(() => {
        if (!cancelled) setAccountUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const sortedAccounts = sortAccountsByName(accounts);
  const accountOptions = sortedAccounts.map((account) => ({ value: String(account.id), label: account.name ?? "-" }));
  const userOptions = accountUsers.map((user) => ({ value: String(user.id), label: userDisplayName(user) }));
  const selectedAccount = sortedAccounts.find((account) => String(account.id) === accountId);

  const setField = (field) => (value) => setDraft((current) => ({ ...current, [field]: value }));

  const buildPayload = () => {
    const payload = {
      name: draft.name.trim(),
      email: draft.email.trim(),
      account: Number(accountId),
      smtp: draft.smtp.trim(),
      smtp_port: draft.smtp_port,
      imap: draft.imap.trim(),
      imap_port: draft.imap_port,
    };
    if (draft.password) payload.password = draft.password;
    if (draft.timezone) payload.timezone = draft.timezone;
    return payload;
  };

  const assignUsersAfterCreate = async (created, currentUsers) => {
    const creatorAssignment = currentUsers[0] ?? null;
    const selectedUserId = assignmentDraft.userId ? Number(assignmentDraft.userId) : null;
    const selectedUserIsCreator = Boolean(creatorAssignment) && selectedUserId === creatorAssignment.user_id;
    const flags = {
      is_mail_account_admin: assignmentDraft.is_mail_account_admin,
      can_send: assignmentDraft.can_send,
      can_receive: assignmentDraft.can_receive,
    };
    if (selectedUserId && !selectedUserIsCreator) {
      currentUsers.push(await usersService.addMailCorpUser(created.id, { user_id: selectedUserId, ...flags }));
    }
    if (!creatorAssignment) return;
    if (selectedUserIsCreator) {
      currentUsers[0] = await usersService.updateMailCorpUserAssignment(created.id, creatorAssignment.id, flags);
    } else if (!keepOwnAccess) {
      await usersService.removeMailCorpUser(created.id, creatorAssignment.id);
      currentUsers.splice(currentUsers.indexOf(creatorAssignment), 1);
    }
  };

  const toListItem = (created, users) => ({
    ...created,
    account: Number(accountId),
    account_name: selectedAccount?.name ?? null,
    oauth_account: created.oauth_account ?? null,
    business_hours: created.business_hours ?? [],
    users,
  });

  const handleCreate = async () => {
    if (!accountId) {
      setFormError("Elegí la cuenta a la que pertenece la casilla.");
      return;
    }
    if (!draft.email.trim()) {
      setFormError("El correo de la casilla es obligatorio.");
      return;
    }
    setSaving(true);
    setFormError(null);
    let created;
    try {
      created = await usersService.createMailCorp(buildPayload());
    } catch (error) {
      setFormError(extractApiErrorMessage(error) || "No se pudo crear la casilla.");
      setSaving(false);
      return;
    }
    const currentUsers = [...(created.users ?? [])];
    try {
      await assignUsersAfterCreate(created, currentUsers);
      onCreated?.(toListItem(created, currentUsers));
      onClose?.();
    } catch (error) {
      const item = toListItem(created, currentUsers);
      setCreatedMailAccount(item);
      onCreated?.(item);
      setFormError(
        `${extractApiErrorMessage(error) || "No se pudieron asignar los usuarios."} La casilla ya quedó creada: terminá la asignación desde su detalle.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const renderAssignmentSection = () => (
    <>
      <div className={styles.mailConfigSectionTitle}>Usuario con acceso</div>
      {loadingUsers ? (
        <div className={styles.noAccounts}>Cargando usuarios de la cuenta...</div>
      ) : (
        <DropdownSelect
          placeholder="Asignar a..."
          options={userOptions}
          value={assignmentDraft.userId}
          onChange={(value) => setAssignmentDraft((current) => ({ ...current, userId: value }))}
          disabled={!accountId}
        />
      )}
      {accountId && !loadingUsers && userOptions.length === 0 && (
        <div className={styles.noAccounts}>La cuenta no tiene usuarios para asignar.</div>
      )}
      {assignmentDraft.userId && (
        <MailAccountAssignmentFlags
          values={assignmentDraft}
          onToggle={(field) => setAssignmentDraft((current) => ({ ...current, [field]: !current[field] }))}
          labelSuffix="para el usuario asignado"
        />
      )}
      <div className={styles.mailToggleRow}>
        <Toggle checked={keepOwnAccess} onChange={setKeepOwnAccess} ariaLabel="Mantener mi acceso a la casilla" />
        <span>Mantener mi acceso a la casilla</span>
      </div>
      <div className={styles.mailConfigHint}>
        Si elegís tu propio usuario, tu acceso queda con los permisos marcados. Para Google o Microsoft, el usuario asignado
        conecta la casilla desde Configuración, en Casillas vinculadas.
      </div>
    </>
  );

  return (
    <ModalOverlay className={styles.overlay} onClose={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerInfo}>
            <div className={styles.modalName}>Nueva casilla</div>
            <div className={styles.modalUsername}>
              <span>Se crea en la cuenta elegida y se asigna al usuario que indiques</span>
            </div>
          </div>
        </div>

        <div className={styles.mailAccountsSection}>
          {formError && (
            <div className={styles.formError} role="alert">
              {formError}
            </div>
          )}

          <div className={styles.mailConfigForm}>
            <DropdownSelect
              label="Cuenta"
              placeholder="Cuenta..."
              options={accountOptions}
              value={accountId}
              onChange={setAccountId}
              disabled={Boolean(createdMailAccount)}
            />

            <div className={styles.mailConfigGrid}>
              <BaseInput label="Nombre" value={draft.name} onChange={setField("name")} disabled={Boolean(createdMailAccount)} />
              <BaseInput
                label="Correo"
                type="email"
                value={draft.email}
                onChange={setField("email")}
                required
                disabled={Boolean(createdMailAccount)}
              />
              <BaseInput
                label="Contraseña"
                type="password"
                value={draft.password}
                onChange={setField("password")}
                validateOnChange={false}
                disabled={Boolean(createdMailAccount)}
              />
              <BaseInput label="SMTP" value={draft.smtp} onChange={setField("smtp")} disabled={Boolean(createdMailAccount)} />
              <BaseInput
                label="Puerto SMTP"
                type="number"
                value={draft.smtp_port}
                onChange={setField("smtp_port")}
                disabled={Boolean(createdMailAccount)}
              />
              <BaseInput label="IMAP" value={draft.imap} onChange={setField("imap")} disabled={Boolean(createdMailAccount)} />
              <BaseInput
                label="Puerto IMAP"
                type="number"
                value={draft.imap_port}
                onChange={setField("imap_port")}
                disabled={Boolean(createdMailAccount)}
              />
            </div>

            <TimezoneSelect value={draft.timezone} onChange={setField("timezone")} />
            <div className={styles.mailConfigHint}>Sin zona horaria, la casilla hereda la zona y los horarios laborales de la cuenta.</div>
          </div>

          <div className={styles.mailConfigForm}>{renderAssignmentSection()}</div>

          <div className={styles.mailInlineActions}>
            {createdMailAccount ? (
              <button type="button" className={styles.saveBtn} onClick={onClose}>
                Cerrar
              </button>
            ) : (
              <>
                <button type="button" className={styles.saveBtn} onClick={handleCreate} disabled={saving}>
                  {saving ? "Creando..." : "Crear casilla"}
                </button>
                <button type="button" className={styles.editBtn} onClick={onClose} disabled={saving}>
                  Cancelar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
